/* The main vendor application form. Extracted from VendorApply.jsx to
   keep the page component small. All state is owned by the parent. */
import { Send } from "lucide-react";
import SlotPicker from "@/components/SlotPicker";
import MediaDropzone from "@/components/MediaDropzone";
import {
  SERVICE_OPTIONS,
  REGION_OPTIONS,
  COVERAGE_OPTIONS,
  PRICING_STRUCTURE,
  PACKAGE_OPTIONS,
  INSURANCE_OPTIONS,
  BUSINESS_OPTIONS,
  YESNO,
  ACCURACY_OPTIONS,
  RESPONSIBILITY_OPTIONS,
} from "@/components/vendor-apply/constants";
import {
  Field,
  TextInput,
  TextArea,
  Checkboxes,
  Radios,
} from "@/components/vendor-apply/FormFields";
import { ProfilePictureField } from "@/components/vendor-apply/ProfilePictureField";

export const VendorApplyForm = ({
  form,
  setForm,
  set,
  onSubmit,
  submitting,
  mediaFiles,
  setMediaFiles,
  mediaOrientation,
  setMediaOrientation,
  onSaveDraft,
  savingDraft,
  draftLastSavedAt,
}) => (
          <form
            data-testid="vendor-apply-form"
            onSubmit={onSubmit}
            className="mt-12 space-y-12"
          >
            {/* Section 1 — About you */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                About you
              </h3>
              <Field label="Full Name" required testId="q-full-name">
                <TextInput
                  id="full_name"
                  value={form.full_name}
                  onChange={set("full_name")}
                />
              </Field>
              <Field
                label="Name of the Company"
                required
                testId="q-company-name"
              >
                <TextInput
                  id="company_name"
                  value={form.company_name}
                  onChange={set("company_name")}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Primary Email Address"
                  required
                  hint="Linked to your sign-in account. Contact us if this needs to change."
                  testId="q-email"
                >
                  <TextInput
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    readOnly
                  />
                </Field>
                <Field label="Contact Number" required testId="q-contact">
                  <TextInput
                    id="contact_number"
                    type="tel"
                    value={form.contact_number}
                    onChange={set("contact_number")}
                  />
                </Field>
              </div>
            </section>

            {/* Section 2 — Service & coverage */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Service & coverage
              </h3>
              <Field
                label="Type of Wedding Service offered"
                required
                hint="Select all that apply."
                testId="q-services"
              >
                <Checkboxes
                  id="service_types"
                  options={SERVICE_OPTIONS}
                  value={form.service_types}
                  onChange={set("service_types")}
                />
              </Field>
              <Field
                label="Where are you based?"
                required
                hint="Select all that apply."
                testId="q-based"
              >
                <Checkboxes
                  id="based_in"
                  options={REGION_OPTIONS}
                  value={form.based_in}
                  onChange={set("based_in")}
                />
              </Field>
              <Field
                label="Where are you willing to cover?"
                required
                testId="q-coverage"
              >
                <Radios
                  id="coverage"
                  options={COVERAGE_OPTIONS}
                  value={form.coverage}
                  onChange={set("coverage")}
                />
              </Field>
            </section>

            {/* Section 3 — Pricing */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Pricing & packages
              </h3>
              <Field
                label="What is your pricing structure?"
                required
                testId="q-pricing"
              >
                <Radios
                  id="pricing_structure"
                  options={PRICING_STRUCTURE}
                  value={form.pricing_structure}
                  onChange={set("pricing_structure")}
                />
              </Field>
              <Field
                label="What is your starting price?"
                required
                hint="E.g. £500, £1,500+, From £2,000."
                testId="q-starting-price"
              >
                <TextInput
                  id="starting_price"
                  value={form.starting_price}
                  onChange={set("starting_price")}
                />
              </Field>
              <Field
                label="How many weddings have you completed?"
                hint="An estimate is fine — e.g. 100+."
                testId="q-weddings-completed"
              >
                <TextInput
                  id="weddings_completed"
                  value={form.weddings_completed}
                  onChange={set("weddings_completed")}
                  placeholder="e.g. 100+"
                />
              </Field>
              <Field
                label="Do you offer packages, add-ons, or bespoke options?"
                required
                testId="q-packages"
              >
                <Radios
                  id="packages_addons"
                  options={PACKAGE_OPTIONS}
                  value={form.packages_addons}
                  onChange={set("packages_addons")}
                />
              </Field>
              <Field
                label="Do you have Public Liability Insurance?"
                required
                testId="q-insurance"
              >
                <Radios
                  id="public_liability_insurance"
                  options={INSURANCE_OPTIONS}
                  value={form.public_liability_insurance}
                  onChange={set("public_liability_insurance")}
                />
              </Field>
              <Field
                label="Short introduction for couples about your business"
                required
                testId="q-intro"
              >
                <TextArea
                  id="short_intro"
                  value={form.short_intro}
                  onChange={set("short_intro")}
                  rows={5}
                  placeholder="A few sentences couples can read at a glance."
                />
              </Field>

              <Field
                label="Attach up to 10 images and/or videos in the same layout (portrait or landscape)"
                hint="Keeps your profile gallery looking unified. All files must share the same orientation — the gallery locks to the orientation of your first upload."
                testId="q-media"
              >
                <MediaDropzone
                  files={mediaFiles}
                  onChange={setMediaFiles}
                  orientation={mediaOrientation}
                  onOrientationChange={setMediaOrientation}
                />
              </Field>

              <Field
                label="Upload one portrait picture of you/team that you want to use."
                hint="This will be the profile picture shown on your vendor card and detail page."
                testId="q-profile-picture"
              >
                <ProfilePictureField
                  value={form.profile_picture_url}
                  onChange={set("profile_picture_url")}
                />
              </Field>
            </section>

            {/* Section 4 — Business */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Business registration
              </h3>
              <Field
                label="Are you operating as a registered business?"
                required
                testId="q-business"
              >
                <Radios
                  id="business_registration"
                  options={BUSINESS_OPTIONS}
                  value={form.business_registration}
                  onChange={set("business_registration")}
                />
              </Field>
            </section>

            {/* Section 5 — Declarations */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Accuracy of Information & Declarations
              </h3>
              <p className="text-sm text-[var(--ld-ink-muted)] leading-relaxed">
                Please note we will email you if we accept your application
                and will require photos and videos you would like under your
                business. Further details will be on the email.
              </p>
              <Field
                label="Do you confirm that all information provided is accurate and up to date?"
                required
                testId="q-accuracy"
              >
                <Radios
                  id="accuracy_confirmed"
                  options={ACCURACY_OPTIONS}
                  value={form.accuracy_confirmed}
                  onChange={set("accuracy_confirmed")}
                />
              </Field>
              <Field
                label="Do you agree that you are solely responsible for services provided to couples, including contracts, payments, and disputes?"
                required
                testId="q-responsibility"
              >
                <Radios
                  id="responsibility_agreed"
                  options={RESPONSIBILITY_OPTIONS}
                  value={form.responsibility_agreed}
                  onChange={set("responsibility_agreed")}
                />
              </Field>
              <Field
                label="Do you consent to your business details being displayed publicly and processed in line with our Privacy Policy?"
                required
                testId="q-public-display"
              >
                <Radios
                  id="public_display_consent"
                  options={YESNO}
                  value={form.public_display_consent}
                  onChange={set("public_display_consent")}
                />
              </Field>
              <Field
                label="Do you confirm that you own the rights or have permission to use all images, videos, and content submitted to the directory?"
                required
                testId="q-content-rights"
              >
                <Radios
                  id="content_rights_confirmed"
                  options={YESNO}
                  value={form.content_rights_confirmed}
                  onChange={set("content_rights_confirmed")}
                />
              </Field>
              <Field
                label="Do you grant permission for the directory to use your submitted content for marketing and promotional purposes?"
                required
                testId="q-marketing"
              >
                <Radios
                  id="marketing_consent"
                  options={YESNO}
                  value={form.marketing_consent}
                  onChange={set("marketing_consent")}
                />
              </Field>
              <Field
                label="Do you confirm that you comply with all applicable UK laws, regulations, and industry standards related to your services?"
                required
                testId="q-uk-compliance"
              >
                <Radios
                  id="uk_compliance_confirmed"
                  options={YESNO}
                  value={form.uk_compliance_confirmed}
                  onChange={set("uk_compliance_confirmed")}
                />
              </Field>

              <Field
                label="Other details (optional)"
                hint="Anything else we should know — e.g. specifying 'Other' selections above."
                testId="q-other"
              >
                <TextArea
                  id="other_details"
                  value={form.other_details}
                  onChange={set("other_details")}
                  rows={4}
                />
              </Field>

              <Field
                label="Book your introduction appointment"
                required
                hint="Pick a slot that suits you. Reserved slots disappear automatically once another vendor or couple takes them."
                testId="q-intro-slot"
              >
                <p
                  data-testid="slot-availability-note"
                  className="mb-4 text-[13px] leading-[1.7] text-[var(--ld-ink)]/85 bg-[var(--ld-gold-soft)]/10 border-l-2 border-[var(--ld-gold-deep)] pl-3 py-2"
                >
                  Please pick a slot to submit your application. If none are
                  showing below, contact Lux directly and we&apos;ll arrange
                  a time.
                </p>
                <SlotPicker
                  role="vendor"
                  testId="vendor-slot-picker"
                  value={
                    form.intro_slot_date && form.intro_slot_time
                      ? {
                          date: form.intro_slot_date,
                          time: form.intro_slot_time,
                        }
                      : null
                  }
                  onChange={(next) => {
                    setForm((prev) => ({
                      ...prev,
                      intro_slot_date: next?.date || "",
                      intro_slot_time: next?.time || "",
                    }));
                  }}
                />
              </Field>
            </section>

            {/* Declaration */}
            <section
              data-testid="declaration-section"
              className="bg-white/40 border border-[var(--ld-border)] p-5 md:p-7"
            >
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)] mb-3">
                Declaration
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="declaration-checkbox"
                  checked={form.declaration_confirmed}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      declaration_confirmed: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 accent-[var(--ld-ink)]"
                />
                <span className="text-[14px] leading-[1.7] text-[var(--ld-ink)]/85">
                  I confirm all the information I have input are correct. An
                  email will be sent with the introduction appointment details.
                  I have read everything and completed it to the best of my
                  knowledge.
                </span>
              </label>
            </section>

            {/* Submit */}
            <div className="flex flex-col items-center gap-3 pt-4">
              {draftLastSavedAt && (
                <p
                  data-testid="vendor-draft-status"
                  className="text-[11px] tracking-[0.18em] uppercase text-[var(--ld-gold-deep)]"
                >
                  Draft saved · {new Date(draftLastSavedAt).toLocaleTimeString()}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                {onSaveDraft && (
                  <button
                    type="button"
                    onClick={onSaveDraft}
                    disabled={savingDraft}
                    data-testid="apply-save-draft"
                    className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[12px] tracking-[0.2em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)] transition-colors duration-500 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingDraft ? "Saving draft…" : "Save draft"}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting || !form.declaration_confirmed}
                  data-testid="apply-submit"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] transition-colors duration-500 hover:text-[#e9d59a] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={14} strokeWidth={1.6} />
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
              </div>
            </div>
          </form>
);
